import { useContext, useState, type MouseEvent } from "react";
import { List, ListItemButton, Box, type ListItemButtonProps } from "@mui/material";
import { NavLink, type NavLinkProps } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import MonitorIcon from "@mui/icons-material/Monitor";
import TelegramIcon from "@mui/icons-material/Telegram";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import ArticleIcon from "@mui/icons-material/Article";
import { styled } from "@mui/system";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import BusinessIcon from "@mui/icons-material/Business";
import { TokenContext } from "../TokenContext";
import { MenuRole } from "../role";

type NavListItemButtonProps = ListItemButtonProps<
  typeof NavLink,
  NavLinkProps
>;

const StyledListItemButton = styled(ListItemButton)<NavListItemButtonProps>({
  backgroundColor: "white",
  color: "#606060",
  fontSize: "20px",
  fontWeight: "600",
  height: "56px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#E6ECF1",
  },

  "& .icon": {
    color: "#0752a3",
  },

  "&.active": {
    backgroundColor: "#0752a3",
    color: "white",
    fontWeight: "600",
    "& .icon": {
      color: "white",
    },
    "&:hover": {
      backgroundColor: "#3874b5",
    },
    "&:active": {
      backgroundColor: "#0752a3",
    },
  },
});

function SideBar() {
  const tokenContext = useContext(TokenContext);
  const user = tokenContext?.user;

  const [expanded1, setExpanded1] = useState(false);
  const [expanded2, setExpanded2] = useState(false);

  if (!user) {
    return null;
  }

  const menuRole = MenuRole(user.m_organization.m_role_id);

  const closePulldown1 = () => {
    setExpanded1(false);
  };

  const toggleHeight1 = (event: MouseEvent) => {
    event.preventDefault();
    setExpanded1(!expanded1);
  };

  const closePulldown2 = () => {
    setExpanded2(false);
  };

  const toggleHeight2 = (event: MouseEvent) => {
    event.preventDefault();
    setExpanded2(!expanded2);
  };

  const closePulldown = () => {
    closePulldown1();
    closePulldown2();
  };

  const closePulldown1And2 = () => {
    closePulldown1();
    closePulldown2();
  };

  const closePulldown1And3 = () => {
    closePulldown1();
  };

  const closePulldown2And3 = () => {
    closePulldown2();
  };

  return (
    <List
      sx={{ width: "272px", p: 0, zIndex: 400, boxShadow: 3, height: "100%" }}
    >
      <StyledListItemButton
        component={NavLink}
        end
        to="/dashboard"
        onClick={() => {
          closePulldown();
        }}
        disabled={!menuRole.dashboard}
        name="toDashboardButton"
      >
        <HomeIcon />
        ダッシュボード
      </StyledListItemButton>

      <StyledListItemButton
        component={NavLink}
        end
        to="/sslog"
        onClick={closePulldown}
        disabled={!menuRole.sslog}
        name="toSsLogButton"
      >
        <HistoryEduIcon />
        SS被害履歴
      </StyledListItemButton>

      <StyledListItemButton
        component={NavLink}
        end
        to="/ssstate"
        onClick={closePulldown}
        disabled={!menuRole.ssstate}
        name="toSsStateButton"
      >
        <MonitorIcon />
        SS稼働状況
      </StyledListItemButton>

      <StyledListItemButton
        component={NavLink}
        end
        to="/ssinfo"
        onClick={closePulldown}
        disabled={!menuRole.ssinfo}
        name="toSsInfoButton"
      >
        <ArticleIcon />
        SS基本情報一覧
      </StyledListItemButton>

      <Box
        className="parentPulldown"
        sx={{
          overflow: "hidden",
          height: expanded1 ? "168px" : "56px",
          transition: "height 0.3s ease",
        }}
      >
        <StyledListItemButton
          component={NavLink}
          end
          to="/mail"
          onClick={toggleHeight1}
          disabled={!menuRole.mail}
          sx={{
            padding: "8px 16px",
            backgroundColor: expanded1 ? "primary.main" : "#FFF",
            color: expanded1 ? "#FFF" : "#606060",
            fontSize: "20px",
            fontWeight: "600",
            height: "56px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            whiteSpace: "nowrap",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: expanded1 ? "#3874b5" : "#E6ECF1",
            },
            ".dropUpIcon": {
              display: expanded1 ? "unset" : "none",
            },
            ".dropDownIcon": {
              display: expanded1 ? "none" : "unset",
            },
          }}
          name="openMailContentsButton"
        >
          <TelegramIcon />
          メール設定
          <Box
            className="dropDownIcon"
            sx={{ right: -32, position: "relative" }}
          >
            <ArrowDropDownIcon />
          </Box>
          <Box className="dropUpIcon" sx={{ right: -32, position: "relative" }}>
            <ArrowDropUpIcon />
          </Box>
        </StyledListItemButton>
        <StyledListItemButton
          className="pulldown1"
          component={NavLink}
          end
          to="/mail/log"
          onClick={closePulldown2And3}
          disabled={!menuRole.mail_send}
          name="toMailLogButton"
        >
          メール送信
        </StyledListItemButton>
        <StyledListItemButton
          className="pulldown1"
          component={NavLink}
          end
          to="/mail/template"
          onClick={closePulldown2And3}
          disabled={!menuRole.mail_template}
          name="toMailTemplateButton"
        >
          テンプレート
        </StyledListItemButton>
      </Box>

      <Box
        className="parentPulldown"
        sx={{
          overflow: "hidden",
          height: expanded2 ? "168px" : "56px",
          transition: "height 0.3s ease",
        }}
      >
        <StyledListItemButton
          component={NavLink}
          end
          to="/account"
          onClick={toggleHeight2}
          disabled={!menuRole.account}
          sx={{
            padding: "8px 16px",
            backgroundColor: expanded2 ? "primary.main" : "#FFF",
            color: expanded2 ? "#FFF" : "#606060",
            fontSize: "20px",
            fontWeight: "600",
            height: "56px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            whiteSpace: "nowrap",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: expanded2 ? "#3874b5" : "#E6ECF1",
            },
            ".dropUpIcon": {
              display: expanded2 ? "unset" : "none",
            },
            ".dropDownIcon": {
              display: expanded2 ? "none" : "unset",
            },
          }}
          name="openAccountContentsButton"
        >
          <PersonIcon />
          アカウント
          <Box
            className="dropDownIcon"
            sx={{ right: -32, position: "relative" }}
          >
            <ArrowDropDownIcon />
          </Box>
          <Box className="dropUpIcon" sx={{ right: -32, position: "relative" }}>
            <ArrowDropUpIcon />
          </Box>
        </StyledListItemButton>
        <StyledListItemButton
          className="pulldown1"
          component={NavLink}
          end
          to="/account/list"
          onClick={closePulldown1And3}
          disabled={!menuRole.account}
          name="toAccountListButton"
        >
          アカウント一覧
        </StyledListItemButton>
        <StyledListItemButton
          className="pulldown1"
          component={NavLink}
          end
          to="/account/authority"
          onClick={closePulldown1And3}
          disabled={!menuRole.authority}
          name="toAccountAuthorityButton"
        >
          権限設定
        </StyledListItemButton>
      </Box>

      <StyledListItemButton
        component={NavLink}
        end
        to="/setting"
        onClick={closePulldown}
        disabled={!menuRole.setting}
        name="toSettingButton"
      >
        <SettingsIcon />
        設定
      </StyledListItemButton>

      <StyledListItemButton
        className="pulldown1"
        component={NavLink}
        end
        to="/tenant"
        onClick={closePulldown1And2}
        disabled={!menuRole.tenant}
        name="toTenantButton"
      >
        <BusinessIcon />
        テナント管理
      </StyledListItemButton>
    </List>
  );
}

export default SideBar;
